import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import {
  IDispositivoLorawan,
  IDispositivoLuminariaACTIS,
  IDispositivoLuminariaGPE,
  ILuminaria,
  TipoValoresReporte,
} from 'modelos/src';
import { AuxiliaresModule } from '../../../../../auxiliares/auxiliares.module';
import { ListadoReportesGenericosComponent } from '../../../../standalone/listado-reportes-genericos/listado-reportes-genericos.component';
import { DetalleMetricasDownlinksComponent } from '../../../luminarias/luminarias/detalles-luminaria/detalle-metricas-downlinks/detalle-metricas-downlinks.component';
import { FilaNSComponent } from './fila-ns/fila-ns.component';
import { DispositivosLorawanService } from '../dispositivos-lorawan.service';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { LuminariasHttpService } from '../../../../../auxiliares/servicios/http/luminarias.service';
import { LuminariasService } from '../../../luminarias/service';
import {
  PIEZAS_DETALLE,
  TonoEstado,
} from '../../../../../componentes/detalle/detalle.components';
import {
  duracion,
  haceCuanto,
} from '../../../../../auxiliares/estado-conexion.util';

interface Fila {
  etiqueta: string;
  valor?: string | null;
  mono?: boolean;
  copiable?: boolean;
}

/** Lo que muestra la pestaña Info, ya armado para la plantilla. */
interface VistaDispositivo {
  comunicacion: { tono: TonoEstado; etiqueta: string };
  hace?: string;
  ultimaComunicacion?: string;
  senal?: string;
  entrega?: { tono: TonoEstado; etiqueta: string };
  equipo: Fila[];
  comunicacionFilas: Fila[];
  paquetes: Fila[];
  comando?: { tono: TonoEstado; estado: string; filas: Fila[] };
  configuracion: Fila[];
}

@Component({
  imports: [
    CommonModule,
    AuxiliaresModule,
    ListadoReportesGenericosComponent,
    DetalleMetricasDownlinksComponent,
    FilaNSComponent,
    ...PIEZAS_DETALLE,
  ],
  selector: 'app-detalles-dispositivo-lorawan',
  templateUrl: './detalles-dispositivo-lorawan.component.html',
  styleUrls: [
    '../../../../../componentes/detalle/detalle-pagina.scss',
    './detalles-dispositivo-lorawan.component.scss',
  ],
})
export class DetallesDispositivoLorawanComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private service = inject(DispositivosLorawanService);
  private helper = inject(HelperService);
  private luminariasService = inject(LuminariasService);
  private luminariasHttp = inject(LuminariasHttpService);

  public dispositivo?: IDispositivoLorawan;
  public vista?: VistaDispositivo;
  /** La luminaria que tiene este dispositivo (por deveui, como el listado). */
  public luminaria?: ILuminaria;
  public cargandoLuminaria = false;
  public loading = true;
  /** No existe o no hay acceso: antes quedaba la página en blanco. */
  public noEncontrado = false;
  public selectedTab = 0;
  private params$?: Subscription;
  private queryParams$?: Subscription;
  /** El `tab` de la URL: se resuelve al cargar (consumo depende del tipo). */
  private tabPedida?: string;

  // El mismo permiso que pide el listado para editar
  public puedeEditar = this.helper.tienePermiso(
    'Administración - Editar dispositivos Lorawan',
  );

  // La solapa de fila del network server es exclusiva del cliente raíz: es
  // estado interno del NS y su acción borra downlinks.
  public esNivel0 = false;

  // Arrays ESTABLES (referencia fija). No usar literales inline en el template:
  // un [tipoValoresReporte]="[x]" crea un array nuevo por ciclo de change
  // detection y, como el listado re-lista en ngOnChanges, tilda la vista.
  public tiposReporteEstado: TipoValoresReporte[] = [];
  public tiposReporteConsumo: TipoValoresReporte[] = [];

  get esConConsumo(): boolean {
    return this.tiposReporteConsumo.length > 0;
  }

  /** El DevEUI de subtítulo, si el título es el nombre. */
  get subtitulo(): string | undefined {
    return this.dispositivo?.name ? this.dispositivo.deveui : undefined;
  }

  /**
   * Las pestañas en orden, por nombre: consumo solo en los tipos que lo
   * reportan y la fila del NS solo para nivel 0. Tiene que coincidir con la
   * plantilla.
   */
  private pestanas(): string[] {
    return [
      'info',
      'estado',
      ...(this.esConConsumo ? ['consumo'] : []),
      'downlinks',
      ...(this.esNivel0 ? ['filaNS'] : []),
    ];
  }

  public tabChanged(event: MatTabChangeEvent): void {
    this.selectedTab = event.index;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: this.pestanas()[event.index] ?? event.index },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  /** `tab` por nombre, o por índice. */
  private indicePestana(tab: string): number | null {
    if (/^\d+$/.test(tab)) return parseInt(tab);
    const i = this.pestanas().indexOf(tab);
    return i >= 0 ? i : null;
  }

  /**
   * Carga el dispositivo del id de la ruta. Se llama cada vez que cambia el
   * id: Angular reutiliza el componente al ir de un dispositivo a otro.
   */
  private async cargar(id: string | null): Promise<void> {
    this.loading = true;
    this.noEncontrado = false;
    this.dispositivo = undefined;
    this.vista = undefined;
    this.luminaria = undefined;
    this.tiposReporteEstado = [];
    this.tiposReporteConsumo = [];
    if (!id) {
      this.noEncontrado = true;
      this.loading = false;
      return;
    }
    try {
      // getFiltered (en vez de getById) para poder popular el modelo del
      // dispositivo (marca/modelo) y el cliente.
      const res = await this.service.getFiltered({
        filter: JSON.stringify({ _id: id }),
        populate: JSON.stringify([
          { path: 'modeloDispositivo', select: 'marca modelo' },
          { path: 'cliente', select: 'nombre' },
        ]),
        limit: 1,
        // MISMO alcance que el listado, que sí lo manda. Sin esto, el filtro de
        // permisos del backend se acota al cliente propio y un dispositivo de un
        // cliente HIJO aparece en la lista pero el detalle devuelve 0 filas →
        // "No se encontró el dispositivo" y la pantalla queda en blanco.
        includeChildren: true,
      });
      this.dispositivo = res.datos?.[0];
    } catch (error) {
      this.helper.notifError(error);
    }
    if (!this.dispositivo) {
      this.noEncontrado = true;
    } else {
      this.definirTiposReporte();
      this.armarVista();
      void this.listarLuminaria();
    }
    this.aplicarTab();
    this.loading = false;
  }

  private aplicarTab(): void {
    const indice = this.tabPedida ? this.indicePestana(this.tabPedida) : null;
    if (indice !== null) this.selectedTab = indice;
  }

  /** La luminaria asignada, por deveui (como la columna del listado). */
  private async listarLuminaria(): Promise<void> {
    const deveui = this.dispositivo?.deveui;
    if (!deveui) return;
    this.cargandoLuminaria = true;
    try {
      const res = await this.luminariasHttp.getFiltered({
        filter: JSON.stringify({ deveui }),
        select: 'identificacion deveui idPuesta idCliente',
        populate: JSON.stringify([
          { path: 'puesta', select: 'identificacion' },
        ]),
        limit: 1,
        includeChildren: true,
      });
      this.luminaria = res.datos?.[0];
    } catch (error) {
      console.error('Error al buscar la luminaria del dispositivo:', error);
    } finally {
      this.cargandoLuminaria = false;
    }
  }

  public verLuminaria(): void {
    if (!this.luminaria?._id) return;
    this.router.navigate([
      'main/luminarias/luminarias/detalles',
      this.luminaria._id,
    ]);
  }

  public editar(): void {
    if (!this.dispositivo?._id) return;
    this.router.navigate([
      'main/administracion/dispositivoslorawan/editar',
      this.dispositivo._id,
    ]);
  }

  // ── Vista ──────────────────────────────────────────────────────────────
  private armarVista(): void {
    const d = this.dispositivo;
    if (!d) return;
    const fecha = d.fechaUltimaComunicacion;
    const tasa = this.tasaEntrega(d);
    const frec = this.getFrecuenciaReporte(d);
    const g = d.ultimoGateway;
    const p = d.paquetes;
    const marcaModelo = [
      d.modeloDispositivo?.marca,
      d.modeloDispositivo?.modelo,
    ]
      .filter(Boolean)
      .join(' ');
    const mostrarJoin =
      d.joineui && d.joineui.toLowerCase() !== d.deveui?.toLowerCase();
    const decimal = (n: number) =>
      n.toLocaleString('es-AR', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      });

    this.vista = {
      comunicacion: this.estadoComunicacion(d),
      hace: fecha ? haceCuanto(fecha) : undefined,
      ultimaComunicacion: fecha
        ? HelperService.getFechaYHora(fecha)
        : undefined,
      senal: d.margin != null ? `${d.margin} dB` : undefined,
      entrega:
        tasa != null
          ? {
              tono: tasa >= 95 ? 'ok' : tasa >= 80 ? 'advertencia' : 'alerta',
              etiqueta: `${decimal(tasa)} % · ${this.estadoEntrega(tasa)}`,
            }
          : undefined,
      equipo: [
        { etiqueta: 'DevEUI', valor: d.deveui, mono: true, copiable: true },
        ...(mostrarJoin
          ? [
              {
                etiqueta: 'JoinEUI',
                valor: d.joineui,
                mono: true,
                copiable: true,
              },
            ]
          : []),
        { etiqueta: 'DevAddr', valor: d.devAddr, mono: true, copiable: true },
        { etiqueta: 'Marca y modelo', valor: marcaModelo || null },
        {
          etiqueta: 'Application ID',
          valor: d.applicationId,
          mono: true,
          copiable: true,
        },
        {
          etiqueta: 'Device Profile ID',
          valor: d.deviceProfileId,
          mono: true,
          copiable: true,
        },
        {
          etiqueta: 'Fecha de creación',
          valor: d.fechaCreacion
            ? HelperService.getFechaYHora(d.fechaCreacion)
            : null,
        },
      ],
      comunicacionFilas: [
        {
          etiqueta: 'Frecuencia de reporte',
          valor: frec != null ? `cada ${duracion(frec)}` : null,
        },
        {
          etiqueta: 'Sin comunicación a las',
          valor:
            d.tiempoLimiteComunicacion != null
              ? `${d.tiempoLimiteComunicacion} h`
              : null,
        },
        {
          etiqueta: 'Último gateway',
          valor: g?.gatewayEui,
          mono: true,
          copiable: true,
        },
        {
          etiqueta: 'RSSI del último gateway',
          valor: g?.rssi != null ? `${g.rssi} dBm` : null,
        },
        {
          etiqueta: 'SNR del último gateway',
          valor: g?.snr != null ? `${decimal(g.snr)} dB` : null,
        },
        {
          etiqueta: 'Captado por el gateway',
          valor: g?.fechaCaptura
            ? HelperService.getFechaYHora(g.fechaCaptura)
            : null,
        },
      ],
      paquetes: [
        {
          etiqueta: 'Frames recibidos',
          valor: p?.framesRecibidos != null ? `${p.framesRecibidos}` : null,
        },
        {
          etiqueta: 'Frames esperados',
          valor:
            p?.ultimoFcnt != null && p?.fCntInicial != null
              ? `${p.ultimoFcnt - p.fCntInicial + 1}`
              : null,
        },
        {
          etiqueta: 'Pérdida de paquetes',
          valor:
            p?.perdidaPaquetes != null
              ? `${decimal(p.perdidaPaquetes)} %`
              : null,
        },
        {
          etiqueta: 'Último FCnt',
          valor: p?.ultimoFcnt != null ? `${p.ultimoFcnt}` : null,
          mono: true,
        },
        {
          etiqueta: 'FCnt inicial',
          valor: p?.fCntInicial != null ? `${p.fCntInicial}` : null,
          mono: true,
        },
      ],
      comando: this.vistaComando(d),
      // Solo lo informado: una tarjeta con todo en "Sin información" no
      // aporta nada (y sin ningún dato se esconde)
      configuracion: this.filasConfiguracion(d).filter(
        (f) => f.valor != null && f.valor !== '',
      ),
    };
  }

  /**
   * Comunicación con el criterio del listado (getColorComunicacion): demorada
   * pasada una frecuencia de reporte y sin comunicación reciente pasadas dos.
   * Sin frecuencia conocida no se puede juzgar.
   */
  private estadoComunicacion(d: IDispositivoLorawan): {
    tono: TonoEstado;
    etiqueta: string;
  } {
    if (!d.fechaUltimaComunicacion) {
      return { tono: 'apagado', etiqueta: 'Sin comunicación' };
    }
    const frec = this.getFrecuenciaReporte(d);
    if (!frec) return { tono: 'neutro', etiqueta: 'Sin frecuencia de reporte' };
    const diff = Date.now() - new Date(d.fechaUltimaComunicacion).getTime();
    const frecMs = frec * 60 * 1000;
    if (diff > 2 * frecMs) {
      return { tono: 'alerta', etiqueta: 'Sin comunicación reciente' };
    }
    if (diff > frecMs) {
      return { tono: 'advertencia', etiqueta: 'Comunicación demorada' };
    }
    return { tono: 'ok', etiqueta: 'Comunicación reciente' };
  }

  /** Último comando, con los estados de LuminariasService.estiloEstadoComando. */
  private vistaComando(
    d: IDispositivoLorawan,
  ): VistaDispositivo['comando'] | undefined {
    const c = d.ultimoComando;
    if (!c) return undefined;
    const estilo = this.luminariasService.estiloEstadoComando(c.estado);
    const tonos: Record<string, TonoEstado> = {
      Ejecutado: 'ok',
      Recibido: 'ok',
      'No Ejecutado': 'alerta',
      Descartado: 'apagado',
    };
    const fecha = c.fechaActualizacion || c.fechaCreacion;
    return {
      // "No Recibido" (ack false) y "En Cola" no son fallas: naranja
      tono: tonos[c.estado ?? ''] ?? 'advertencia',
      estado: estilo.texto,
      filas: [
        { etiqueta: 'Descripción', valor: c.descripcion || c.nombre || null },
        {
          etiqueta: 'Origen',
          valor: c.objetivo?.nombre
            ? `${c.objetivo.nivel} · ${c.objetivo.nombre}`
            : null,
        },
        {
          etiqueta: 'Puerto',
          valor: c.puerto != null ? `${c.puerto}` : null,
          mono: true,
        },
        {
          etiqueta: 'Fecha',
          valor: fecha ? HelperService.getFechaYHora(fecha) : null,
        },
      ],
    };
  }

  // Configuración del dispositivo, según su tipo. GPE reusa el formateo de la
  // config general de LuminariasService; ACTIS arma reportes/ACK/automatismos.
  private filasConfiguracion(d: IDispositivoLorawan): Fila[] {
    if (d.tipo === 'Luminaria GPE') {
      const cfg = (d.config as IDispositivoLuminariaGPE)?.configGeneral;
      if (!cfg) return [];
      return this.luminariasService
        .formatConfigGeneralGPE(cfg)
        .map((row) => ({ etiqueta: row.label, valor: row.value }));
    }
    if (d.tipo === 'Luminaria ACTIS FING') {
      const cfg = d.config as IDispositivoLuminariaACTIS;
      if (!cfg) return [];
      const r = cfg.configReportes;
      const ack = cfg.configACK;
      const ra = cfg.relojAstronomico;
      const fc = cfg.fotocelula;
      const siNo = (v?: boolean) => (v == null ? null : v ? 'Sí' : 'No');
      const min = (v?: number) => (v != null ? `${v} min` : null);
      return [
        { etiqueta: 'Período de estado', valor: min(r?.periodoEstado) },
        { etiqueta: 'Período de energía', valor: min(r?.periodoEnergia) },
        {
          etiqueta: 'Reportar encendido y apagado',
          valor: siNo(r?.reportarEncendidoApagado),
        },
        {
          etiqueta: 'Período del ACK',
          valor: ack?.periodoHoras != null ? `${ack.periodoHoras} h` : null,
        },
        { etiqueta: 'ACK forzado', valor: siNo(ack?.forzado) },
        { etiqueta: 'Offset del amanecer', valor: min(ra?.offsetAmanecer) },
        { etiqueta: 'Offset del atardecer', valor: min(ra?.offsetAtardecer) },
        {
          etiqueta: 'Umbral inferior de la fotocélula',
          valor: fc?.umbralInferior != null ? `${fc.umbralInferior}` : null,
        },
        {
          etiqueta: 'Umbral superior de la fotocélula',
          valor: fc?.umbralSuperior != null ? `${fc.umbralSuperior}` : null,
        },
      ];
    }
    return [];
  }

  // ── Helpers ────────────────────────────────────────────────────────────
  private tasaEntrega(d: IDispositivoLorawan): number | null {
    const perdida = d.paquetes?.perdidaPaquetes;
    return perdida != null ? 100 - perdida : null;
  }

  private estadoEntrega(tasa: number): string {
    if (tasa >= 95) return 'Excelente';
    if (tasa >= 80) return 'Aceptable';
    return 'Crítico';
  }

  // Frecuencia de reporte (minutos) según el tipo de dispositivo.
  private getFrecuenciaReporte(d: IDispositivoLorawan): number | null {
    if (!d) return null;
    if (d.tipo === 'Luminaria Wellness') return 15;
    if (d.tipo === 'Luminaria GPE') {
      const cfg = (d.config as IDispositivoLuminariaGPE)?.configGeneral;
      return cfg?.frecReporte ?? null;
    }
    if (d.tipo === 'Luminaria ACTIS FING') {
      const cfg = (d.config as IDispositivoLuminariaACTIS)?.configReportes;
      return cfg?.periodoEnergia ?? cfg?.periodoEstado ?? null;
    }
    return null;
  }

  // Define qué tipos de reporte usar según el tipo de dispositivo (mismo criterio que la luminaria)
  private definirTiposReporte(): void {
    const tipo = this.dispositivo?.tipo;
    if (tipo === 'Luminaria GPE') {
      this.tiposReporteEstado = ['Luminaria GPE Periódico'];
      this.tiposReporteConsumo = ['Luminaria GPE Energía'];
    }
    if (tipo === 'Luminaria ACTIS FING') {
      this.tiposReporteEstado = ['Luminaria ACTIS FING Estado'];
      this.tiposReporteConsumo = ['Luminaria ACTIS FING Energía'];
    }
    if (tipo === 'Luminaria CitiLight') {
      this.tiposReporteEstado = ['Luminaria CitiLight Estado'];
      this.tiposReporteConsumo = ['Luminaria CitiLight Energía'];
    }
    if (tipo === 'Luminaria Wellness') {
      this.tiposReporteEstado = ['Luminaria Wellness'];
      // Wellness no tiene reporte de consumo
    }
  }

  ngOnInit(): void {
    this.esNivel0 = this.helper.esNivel0();
    this.params$ = this.route.paramMap.subscribe((params) => {
      void this.cargar(params.get('id'));
    });
    this.queryParams$ = this.route.queryParams.subscribe((params) => {
      this.tabPedida = params['tab'];
      if (!this.loading) this.aplicarTab();
    });
  }

  ngOnDestroy(): void {
    this.params$?.unsubscribe();
    this.queryParams$?.unsubscribe();
  }
}
